/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addScreeningTeamMembers,
  deleteScreeningTeamMembers,
  editScreeningTeamMembers,
} from "services/incidentService";
import { CrimeReference, Roles, ScreeningTeamMember } from "types";
import {
  BASE_URL,
  deleteIcon,
  listIcon,
  LoadingIcon,
  searchIcon,
} from "utils/constant";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  incident: CrimeReference;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeTab: React.Dispatch<React.SetStateAction<string>>;
};

const ScreeningMembers: FC<Props> = ({
  incident,
  setRefetch,
  setChangeTab,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [prosecutors, setProsecutors] = useState<
    {
      user: {
        id: string;
        first_name: string;
        last_name: string;
      };
    }[]
  >([]);
  const [screeningTeamDetail, setScreeningTeamDetails] = useState<
    ScreeningTeamMember[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("List");
  const [memberId, setMemberId] = useState<any>();

  const fetchProsecutorsData = (
    adminId: string | number,
    orgId: string | number,
  ) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor") ||
              d?.roles?.includes("ProsecutionCaseScreeningTeamCoordinator") ||
              d?.roles?.includes(
                "ProsecutionCaseScreeningTeamCoordinatorEconomic",
              ),
          ),
        );
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching prosecutors:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) {
      setLoading(true);
      fetchProsecutorsData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData]);

  useEffect(() => {
    if (incident?.screening_members) {
      setScreeningTeamDetails(incident?.screening_members || []);
    }
  }, [incident]);

  const canManage = hasRole(loginData?.roles, [
    Roles.corruptionDirector,
    Roles.screeningTeamCoordinator,
  ]);
  const memberCount = incident?.screening_members?.length ?? 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header: title, member count badge, and primary action */}
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <div className="text-gray-600">{listIcon}</div>
          <h3 className="font-semibold text-gray-900">
            {t("screening_members")}
          </h3>
          <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-full">
            {memberCount}
          </span>
        </div>
        {canManage && (
          <button
            className="shrink-0 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            onClick={() => setType(type === "List" ? "Add" : "List")}
          >
            {type === "List" ? t("add_member_cta") : t("view_members")}
          </button>
        )}
      </div>

      <p className="text-gray-600 mb-6">{t("manage_screening_team_members")}</p>

      {type === "List" ? (
        memberCount === 0 ? (
          /* Empty state: clarify there are no members yet and what to do */
          <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl">
            <div className="text-4xl mb-3">👥</div>
            <h4 className="font-semibold text-gray-900 mb-1">
              {t("no_screening_members_yet")}
            </h4>
            <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
              {canManage
                ? t("add_members_to_form_team")
                : t("coordinator_access_required_to_add_members")}
            </p>
            {canManage && (
              <button
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                onClick={() => setType("Add")}
              >
                {t("add_member_cta")}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-6">
              {searchIcon}
              <input
                placeholder={t("search_by_name")}
                className="flex-1 bg-transparent focus:outline-none"
                onChange={(e) => {
                  const searchValue = e.target.value.toLowerCase();
                  const filteredData = incident?.screening_members?.filter(
                    (team) =>
                      team?.prosecutor_name
                        ?.toLowerCase()
                        .includes(searchValue),
                  );
                  setScreeningTeamDetails(filteredData || []);
                }}
              />
            </div>

            <div className="space-y-4">
              {screeningTeamDetail?.map((team, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <span className="text-gray-600">👤</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {team?.prosecutor_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {team?.team_leader && (
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          {t("team_lead")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg"
                    onClick={() => {
                      deleteScreeningTeamMembers(team)
                        .then(() => {
                          showTooltip(t("successfully_deleted"), "success");
                          setMemberId(undefined);
                          setType("List");
                          setRefetch((prev) => !prev);
                          setChangeTab("Screening");
                        })
                        .catch((error) => {
                          showTooltip(
                            error?.response?.data?.detail ||
                              errorHandlerOnForm(error),
                            "error",
                          );
                        });
                    }}
                  >
                    {deleteIcon}
                  </button>
                  {!team.team_leader && (
                    <button
                      className="px-3 py-1 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                      onClick={() => {
                        editScreeningTeamMembers({
                          ...team,
                          team_leader: true,
                        })
                          .then(() => {
                            showTooltip(t("successfully_updated"), "success");
                            setRefetch((prev) => !prev);
                            setChangeTab("Screening");
                          })
                          .catch((error) => {
                            showTooltip(
                              error?.response?.data?.detail ||
                                errorHandlerOnForm(error) ||
                                errorHandler(error) ||
                                t("something_went_wrong_try_again"),
                              "error",
                            );
                          });
                      }}
                    >
                      {t("make_lead")}
                    </button>
                  )}
                </div>
              </div>
              ))}
            </div>
          </>
        )
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-gray-500">
            {t("select_prosecutor_to_add")}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("select_prosecutor")}
            </label>
            <select
              value={memberId || ""}
              onChange={(e) => setMemberId(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">{t("select_prosecutor")}</option>
              {prosecutors
                .filter(
                  (prosecutor) =>
                    !incident?.screening_members?.some(
                      (team) =>
                        team.prosecutor_user_id?.toString() ===
                        prosecutor.user?.id?.toString(),
                    ),
                )
                .map((prosecutor, index) => (
                  <option key={index} value={prosecutor.user?.id}>
                    {prosecutor?.user?.first_name +
                      " " +
                      prosecutor?.user?.last_name}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
              onClick={() => {
                setMemberId(undefined);
                setType("List");
              }}
            >
              {t("cancel")}
            </button>
            <button
              className={`flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors
              ${loading || !memberId ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading || !memberId}
              onClick={() => {
                setLoading(true);
              const member = prosecutors.find(
                (p) => p.user.id.toString() === memberId?.toString(),
              )?.user;

              addScreeningTeamMembers({
                crime_reference: incident?.uu_id,
                prosecutor_user_id: memberId + "",
                prosecutor_name: member?.first_name + " " + member?.last_name,
                team_leader: false,
              })
                .then(() => {
                  showTooltip(t("successfully_added"), "success");
                  setMemberId(undefined);
                  setType("List");
                  setRefetch((prev) => !prev);
                  setChangeTab("Screening");
                  setLoading(false);
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail || errorHandlerOnForm(error),
                    "error",
                  );
                  setLoading(false);
                });
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                {LoadingIcon}
                {t("adding")}
              </div>
              ) : (
                t("add_member")
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreeningMembers;
